import { useState } from "react";
import salesmanService from "../../../services/salesman.service";
import { getApiErrorMessage } from "../../../utils/downloadExcel";

export const EMPTY_SALESMAN_FORM = {
  salesmanName: "",
  mobile: "",
  alternateMobile: "",
  email: "",
  distributorName: "",
  address: "",
  state: "",
  city: "",
  pinCode: "",
  description: "",
  isActive: true,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function salesmanFromRecord(row) {
  return {
    salesmanName: row.salesmanName || "",
    mobile: row.mobile || "",
    alternateMobile: row.alternateMobile || "",
    email: row.email || "",
    distributorName: row.distributorName || "",
    address: row.address || "",
    state: row.state || "",
    city: row.city || "",
    pinCode: row.pinCode || "",
    description: row.description || "",
    isActive: row.isActive !== false,
  };
}

function mobileError(value, label, required) {
  const digits = String(value || "").replace(/\D/g, "");
  const mobile = digits.length === 12 && digits.startsWith("91") ? digits.slice(2) : digits;
  if (!mobile) return required ? `${label} is required.` : "";
  if (!/^[6-9]\d{9}$/.test(mobile)) return `${label} must be a 10-digit Indian number.`;
  return "";
}

export default function SalesmanFormModal({
  editingId = "",
  initial = EMPTY_SALESMAN_FORM,
  viewOnly = false,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState(initial);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const setField = (key) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (viewOnly) {
      onClose?.();
      return;
    }
    const salesmanName = form.salesmanName.trim();
    const requiredMobile = mobileError(form.mobile, "Mobile number", true);
    const altError = mobileError(form.alternateMobile, "Alternate mobile number");
    const email = form.email.trim();
    const pinCode = String(form.pinCode || "").replace(/\D/g, "");
    if (!salesmanName) {
      setFormError("Salesman name is required.");
      return;
    }
    if (requiredMobile) {
      setFormError(requiredMobile);
      return;
    }
    if (altError) {
      setFormError(altError);
      return;
    }
    if (email && !EMAIL_RE.test(email)) {
      setFormError("Email must be a valid email address.");
      return;
    }
    if (pinCode && !/^\d{6}$/.test(pinCode)) {
      setFormError("PIN code must be 6 digits.");
      return;
    }

    const payload = {
      salesmanName,
      mobile: form.mobile,
      alternateMobile: form.alternateMobile,
      email,
      distributorName: form.distributorName.trim(),
      address: form.address.trim(),
      state: form.state.trim(),
      city: form.city.trim(),
      pinCode,
      description: form.description.trim(),
      isActive: form.isActive !== false,
    };

    try {
      setSaving(true);
      setFormError("");
      const res = editingId
        ? await salesmanService.update(editingId, payload)
        : await salesmanService.create(payload);
      onSaved?.(res?.data?.data?.record || null);
    } catch (err) {
      setFormError(await getApiErrorMessage(err, "Failed to save salesman."));
      setSaving(false);
    }
  };

  const title = viewOnly
    ? "Salesman Details"
    : editingId
      ? "Edit Salesman"
      : "Add Salesman";

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className="modal-box cbn-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-labelledby="salesman-form-title"
      >
        <h3 id="salesman-form-title">{title}</h3>
        <form onSubmit={handleSubmit}>
          <div className="cbn-form-grid">
            <div className="cbn-span-2">
              <label className="modal-label" htmlFor="sm-name">
                Salesman Name *
              </label>
              <input
                id="sm-name"
                className="modal-input"
                value={form.salesmanName}
                onChange={setField("salesmanName")}
                disabled={viewOnly || saving}
                required
              />
            </div>
            <div>
              <label className="modal-label" htmlFor="sm-mobile">
                Mobile Number *
              </label>
              <input
                id="sm-mobile"
                className="modal-input"
                type="tel"
                value={form.mobile}
                maxLength={10}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    mobile: event.target.value.replace(/\D/g, "").slice(0, 10),
                  }))
                }
                disabled={viewOnly || saving}
                required
              />
            </div>
            <div>
              <label className="modal-label" htmlFor="sm-alt">
                Alternate Mobile Number
              </label>
              <input
                id="sm-alt"
                className="modal-input"
                type="tel"
                value={form.alternateMobile}
                maxLength={10}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    alternateMobile: event.target.value.replace(/\D/g, "").slice(0, 10),
                  }))
                }
                disabled={viewOnly || saving}
              />
            </div>
            <div>
              <label className="modal-label" htmlFor="sm-email">
                Email Address
              </label>
              <input
                id="sm-email"
                className="modal-input"
                type="email"
                value={form.email}
                onChange={setField("email")}
                disabled={viewOnly || saving}
              />
            </div>
            <div>
              <label className="modal-label" htmlFor="sm-company">
                Company / Distributor Name
              </label>
              <input
                id="sm-company"
                className="modal-input"
                value={form.distributorName}
                onChange={setField("distributorName")}
                disabled={viewOnly || saving}
              />
            </div>
            <div className="cbn-span-2">
              <label className="modal-label" htmlFor="sm-address">
                Address
              </label>
              <input
                id="sm-address"
                className="modal-input"
                value={form.address}
                onChange={setField("address")}
                disabled={viewOnly || saving}
              />
            </div>
            <div>
              <label className="modal-label" htmlFor="sm-state">
                State
              </label>
              <input
                id="sm-state"
                className="modal-input"
                value={form.state}
                onChange={setField("state")}
                disabled={viewOnly || saving}
              />
            </div>
            <div>
              <label className="modal-label" htmlFor="sm-city">
                City
              </label>
              <input
                id="sm-city"
                className="modal-input"
                value={form.city}
                onChange={setField("city")}
                disabled={viewOnly || saving}
              />
            </div>
            <div>
              <label className="modal-label" htmlFor="sm-pin">
                PIN Code
              </label>
              <input
                id="sm-pin"
                className="modal-input"
                value={form.pinCode}
                maxLength={6}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    pinCode: event.target.value.replace(/\D/g, "").slice(0, 6),
                  }))
                }
                disabled={viewOnly || saving}
              />
            </div>
            <div>
              <label className="modal-label" htmlFor="sm-status">
                Status
              </label>
              <select
                id="sm-status"
                className="modal-input"
                value={form.isActive ? "active" : "inactive"}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, isActive: event.target.value === "active" }))
                }
                disabled={viewOnly || saving}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="cbn-span-2">
              <label className="modal-label" htmlFor="sm-desc">
                Description
              </label>
              <input
                id="sm-desc"
                className="modal-input"
                value={form.description}
                onChange={setField("description")}
                disabled={viewOnly || saving}
              />
            </div>
          </div>
          {formError ? <p className="error-text cbn-form-error">{formError}</p> : null}
          <div className="modal-actions">
            <button
              type="button"
              className="secondary-page-btn"
              onClick={onClose}
              disabled={saving}
            >
              {viewOnly ? "Close" : "Cancel"}
            </button>
            {viewOnly ? null : (
              <button type="submit" className="primary-page-btn" disabled={saving}>
                {saving ? "Saving…" : editingId ? "Update Salesman" : "Save Salesman"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
